import DescriptionIcon from '@mui/icons-material/DescriptionOutlined';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import { CircularProgress, Typography } from '@mui/material';

import { DocumentThumbnail } from '@/components/feedback/DocumentThumbnail';
import { useDocumentViewer } from '@/components/feedback/useDocumentViewer';
import { usePdfDocument } from '@/components/feedback/usePdfDocument';

interface SignableDocumentProps {
  /** The file being signed. Empty renders the unavailable state. */
  href: string;
  /** Names the document on the card and in the viewer's header, so a director
   *  in a queue can tell which one they are being asked to sign. */
  title: string;
}

/**
 * The document a director is about to sign, on the card that asks them to sign
 * it.
 *
 * Reading is opened into the app's one document viewer — the same
 * `DocumentViewerDialog` the company screens and the outstanding rows use — not
 * into a private reader built for this screen. The flow used to frame the file
 * in a collapsed `<iframe>`, which mobile Safari answers with page one at print
 * size and no way to scroll: the director's real options were to sign unread or
 * to leave the portal for the Files app. Signing is legally binding, and the
 * screen that binds them should have the best reader in the app, not the
 * weakest.
 *
 * The card shows the document's actual first page rather than an icon. A
 * director signing four items in a queue is being asked to confirm *this*
 * document, and a row of identical grey bars cannot tell them apart — the
 * letterhead can. It is also the honest answer to "is the file really there":
 * the page is on screen because the bytes arrived and parsed.
 */
export const SignableDocument = ({ href, title }: SignableDocumentProps) => {
  const { open, viewer } = useDocumentViewer();
  // Fetched as soon as the card mounts, not on a tap. The director is here to
  // sign this document, so it is the one thing on the screen certain to be
  // wanted — and the parsed copy is what the thumbnail is drawn from.
  const pdf = usePdfDocument({ url: href }, href !== '');

  if (href === '') {
    return (
      <div
        className="rounded-2xl bg-surface-subtle px-4 py-3 inset-ring inset-ring-divider"
        role="status"
      >
        <Typography variant="body2" className="text-content-muted">
          This document is still being prepared, so there is nothing to read yet.
        </Typography>
      </div>
    );
  }

  const read = () => {
    open({ source: { url: href }, title });
  };

  return (
    <>
      {/* The whole card is the control. A thumbnail with a separate "Read"
          button beside it gives the eye two targets for one job, and on a phone
          the tappable thing should be the thing you are looking at (§4). */}
      <button
        type="button"
        onClick={read}
        aria-label={`Read ${title}`}
        className="group flex w-full items-stretch gap-3 overflow-hidden rounded-2xl bg-surface p-2.5 text-left shadow-card inset-ring inset-ring-divider"
      >
        {/* A fixed window onto the top of page one, kept short so the card
            costs a phone screen as little height as possible — the signature
            pad and the sign button are what have to stay in reach (§4).
            Cropped rather than fitted, so every card is the same height
            whatever shape its document is. */}
        <div className="relative h-16 w-[3rem] shrink-0 overflow-hidden rounded-md bg-white ring-1 ring-black/5">
          {pdf.status === 'ready' ? (
            <DocumentThumbnail document={pdf.document} alt={`First page of ${title}`} />
          ) : (
            <div className="flex h-full items-center justify-center">
              {pdf.status === 'loading' ? (
                <CircularProgress size={14} />
              ) : (
                /* The document may be an image, an Office file, or on a host
                   that refuses a cross-origin read. None of those can be drawn
                   here, and none of them stop the viewer opening — it renders
                   images and says so honestly for the rest. */
                <DescriptionIcon fontSize="small" className="text-content-disabled" />
              )}
            </div>
          )}
        </div>

        <div className="flex min-w-0 flex-1 flex-col justify-center gap-1">
          {/* A span, not the `<h6>` `subtitle2` maps to. The button's own
              `aria-label` already names the document, and a heading here would
              be the second one carrying this title on the sign screen. */}
          <Typography variant="subtitle2" component="span" className="min-w-0 break-words">
            {title}
          </Typography>

          <span className="flex items-center gap-1.5 text-content-muted">
            <VisibilityIcon fontSize="small" aria-hidden />
            <Typography variant="body2" component="span">
              {/* Names the page count once it is known: it is the one fact that
                  tells a director whether they are about to read a page or
                  fifteen, and it is free by the time the card can say it. */}
              {pdf.status === 'ready'
                ? `Read ${String(pdf.document.numPages)} ${pdf.document.numPages === 1 ? 'page' : 'pages'} before signing`
                : 'Read before signing'}
            </Typography>
          </span>
        </div>
      </button>

      {viewer}
    </>
  );
};
